import { ChangeDetectionStrategy, Component, OnInit, computed, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AdditionalServiceVoice } from '@sales/modules/subscription/models/additional-service-voice.model';
import { IconNames } from '@shared/enums/icon-names';
import { MatIcon } from '@angular/material/icon';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { IceIconButtonComponent } from '@app/shared/components/atoms/ice-icon-button/ice-icon-button.component';
import { AdditionalSimComponent } from './additional-sim/additional-sim.component';
import { MAX_NEW_EXTRA_SIMS_PER_TYPE_IN_ORDER } from '@sales/models/simcard.model';

export interface SimRow {
  instanceId: string;
  form: FormGroup;
}

export interface ActiveSim {
  service: AdditionalServiceVoice;
  label: string;
  form: FormGroup;
}

@Component({
  selector: 'app-additional-sim-group',
  templateUrl: './additional-sim-group.component.html',
  styleUrls: ['./additional-sim-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'group',
    '[attr.aria-label]': 'service().description',
  },
  imports: [ReactiveFormsModule, MatIcon, IceSelectBoxComponent, IceIconButtonComponent, AdditionalSimComponent],
})
export class AdditionalSimGroupComponent implements OnInit {
  IconNames = IconNames;

  service = input.required<AdditionalServiceVoice>();
  rows = input.required<SimRow[]>();
  activeSims = input<ActiveSim[]>([]);

  add = output<void>();
  remove = output<SimRow>();
  clear = output<void>();

  selected = new FormControl(false);

  canAdd = computed(
    () => this.rows().length < Math.min(this.service().availableQuantity ?? 1, MAX_NEW_EXTRA_SIMS_PER_TYPE_IN_ORDER)
  );
  groupId = computed(() => `sim-group-${this.service().productId}`);
  addLabel = computed(() => `Legg til ${this.service().description}`);

  rowViews = computed(() =>
    this.rows().map((row, index) => {
      const label = `${this.service().description} ${this.activeSims().length + index + 1}`;
      return { row, label, removeLabel: `Fjern ${label}` };
    })
  );

  constructor() {
    this.selected.valueChanges.pipe(takeUntilDestroyed()).subscribe(checked => {
      if (!checked) {
        this.clear.emit();
      } else if (!this.rows().length) {
        this.add.emit();
      }
    });
  }

  ngOnInit() {
    this.selected.setValue(this.rows().length > 0 || this.activeSims().length > 0, { emitEvent: false });
  }

  get isGroupSelected(): boolean {
    return !!this.selected.value || this.activeSims().length > 0;
  }

  onRemove(row: SimRow) {
    if (this.rows().length === 1 && !this.activeSims().length) {
      this.selected.setValue(false);
    } else {
      this.remove.emit(row);
    }
  }
}
